import { ref, onMounted, onUnmounted } from 'vue'

/**
 * ============================================================================
 * useFoundationLogoVisibility.js - Gestione Loghi Fondazione/Castello
 * ============================================================================
 * 
 * Composable Vue 3 che controlla quali loghi mostrare nell'header a seconda
 * della pagina corrente e della posizione dello scroll.
 * 
 * COMPORTAMENTO PER PAGINA:
 * ├─ HOME PAGE (/, /home, /cassa, /index):
 * │  ├─ Scroll = 0 (top):
 * │  │  ├─ showFoundationLogo = true  (FONDAZIONE visibile)
 * │  │  └─ showCastleLogo = false     (Castello nascosto)
 * │  │
 * │  └─ Green bar sticky (hero nascosto, scrollY ≥ threshold):
 * │     ├─ showFoundationLogo = false (FONDAZIONE scompare)
 * │     └─ showCastleLogo = true      (CASTELLO appare)
 * │
 * └─ ALTRE PAGINE (es. /prodotti, /contatti):
 *    ├─ showFoundationLogo = false (sempre nascosto)
 *    └─ showCastleLogo = false    (sempre nascosto)
 * 
 * SINCRONIZZAZIONE:
 * • Si coordina con heroGreenBar.js tramite evento 'greenbar:ready'
 * • Throttling con requestAnimationFrame per performance
 * • 2 rAF + window 'load' per evitare flicker al caricamento
 * 
 * THRESHOLD CALCOLO:
 * Sticky quando: scrollY >= (hero.top + hero.height - header.height - 1)
 * Il -1 evita rimbalzi millimetrici al confine
 * ============================================================================
 */

/**
 * Composable per gestire la visibilità dei loghi dell'header
 * 
 * @returns {Object} Oggetto reattivo con:
 *   - showFoundationLogo: Ref<boolean> - Mostra logo Fondazione
 *   - showCastleLogo: Ref<boolean> - Mostra logo Castello
 */
export function useFoundationLogoVisibility () {
  /**
   * Visibilità logo Fondazione
   * @type {Ref<boolean>}
   */
  const showFoundationLogo = ref(false)

  /**
   * Visibilità logo Castello
   * @type {Ref<boolean>}
   */
  const showCastleLogo = ref(false)

  /**
   * Determina se siamo attualmente sulla home page
   * 
   * ROUTE MATCHES:
   * • '/' → true (root)
   * • '/home' → true
   * • '/cassa' → true
   * • '/index' → true
   * • Qualsiasi altra route → false
   * 
   * Normalizza il pathname:
   * • Rimuove trailing slashes
   * • Gestisce radici vuote come '/'
   * 
   * @returns {boolean}
   */
  const getIsHome = () => {
    const normPath = (window.location.pathname || '/').replace(/\/+$/, '') || '/'
    return normPath === '/' || normPath === '/cassa' || normPath === '/home' || normPath === '/index'
  }

  /**
   * Verifica se la green bar è in stato "sticky"
   * 
   * LOGICA:
   * 1. Legge posizioni di hero e header dal DOM
   * 2. Calcola threshold: il punto in cui hero esce completamente da viewport
   *    threshold = hero.top + hero.height - header.height
   * 3. Ritorna true se scrollY ha superato il threshold
   * 4. Il -1 nel confronto evita rimbalzi millimetrici
   * 
   * ESEMPIO:
   * • Hero: top=0, height=800px
   * • Header: height=80px
   * • Threshold = 0 + 800 - 80 = 720px
   * • Green bar sticky quando scrollY ≥ 719px
   * 
   * @returns {boolean} - true se green bar è sticky
   */
  const computeGreenBarSticky = () => {
    const hero = document.querySelector('.section-hero')
    const header = document.querySelector('.site-header')
    if (!hero || !header) return false

    const headerH = header.offsetHeight || 0
    // Soglia reale: quando la parte bassa dell'hero raggiunge il bordo inf. dell'header
    const threshold = hero.offsetTop + hero.offsetHeight - headerH
    // -1 per evitare rimbalzi millimetrici
    return window.scrollY >= (threshold - 1)
  }

  /**
   * Applica lo stato di visibilità dei loghi in base a:
   * • Pagina corrente (home vs altre)
   * • Posizione della green bar (sticky vs absolute)
   * 
   * LOGICA:
   * 1. Se NON siamo in home: nascondi entrambi i loghi
   * 2. Se siamo in home:
   *    - Green bar sticky (scrollY alto): mostra Castello, nascondi Fondazione
   *    - Green bar absolute (scrollY basso): mostra Fondazione, nascondi Castello
   */
  const applyState = () => {
    const isHome = getIsHome()

    if (!isHome) {
      // Fuori dalla home: nascondi tutto
      showFoundationLogo.value = false
      showCastleLogo.value = false
      return
    }

    // In home: gestisci visibility in base allo scroll
    const sticky = computeGreenBarSticky()
    if (sticky) {
      // Green bar sticky (hero nascosto): mostra Castello
      showFoundationLogo.value = false
      showCastleLogo.value = true
    } else {
      // Green bar absolute (hero visibile): mostra Fondazione
      showFoundationLogo.value = true
      showCastleLogo.value = false
    }
  }

  /**
   * Throttling via requestAnimationFrame
   * 
   * Evita di eseguire applyState() ad ogni scroll event.
   * Se un calcolo è già in coda (ticking=true), ignora i nuovi eventi
   * fino a quando non termina il frame corrente.
   * 
   * @type {boolean}
   */
  let ticking = false

  /**
   * Richiede l'esecuzione di applyState() nel prossimo frame
   * Se già in coda, ignora la richiesta
   */
  const scheduleApply = () => {
    if (ticking) return
    ticking = true
    requestAnimationFrame(() => {
      ticking = false
      applyState()
    })
  }

  // Handler per diversi trigger
  const onScroll = () => scheduleApply()
  const onResize = () => scheduleApply()
  const onGreenBarReady = () => scheduleApply()

  /**
   * Setup lifecycle: inizializzazione listeners e stato iniziale
   */
  onMounted(() => {
    // Stato iniziale sicuro: evita flicker
    if (getIsHome()) {
      showFoundationLogo.value = true
      showCastleLogo.value = false
    } else {
      showFoundationLogo.value = false
      showCastleLogo.value = false
    }

    // Primo check dopo 2 frame per layout stabile
    // Questo assicura che il DOM sia completamente renderizzato
    requestAnimationFrame(() => {
      requestAnimationFrame(() => applyState())
    })

    // Riallina quando tutto è carico (immagini, font, risorse esterne)
    window.addEventListener('load', applyState, { once: true })

    // Listener scroll: aggiornamento in real-time (throttled via rAF)
    window.addEventListener('scroll', onScroll, { passive: true })

    // Listener resize: ricalcola threshold quando viewport cambia
    window.addEventListener('resize', onResize)

    // Evento custom da heroGreenBar.js: sincronizza quando green bar è pronta
    // Questo garantisce che i loghi e la green bar siano allineati
    document.addEventListener('greenbar:ready', onGreenBarReady)
  })

  /**
   * Cleanup lifecycle: rimuovi tutti i listener
   */
  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onResize)
    document.removeEventListener('greenbar:ready', onGreenBarReady)
  })

  return {
    showFoundationLogo,
    showCastleLogo
  }
}